import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

type RefType = {
  current: HTMLElement | null;
};

/**
 * Animates the section title with a fade-in and slide-up effect
 */
export const animateTitle = (
  titleRef: RefType,
  containerRef: RefType
): gsap.core.Timeline | undefined => {
  if (!titleRef.current || !containerRef.current) return;

  const tl = gsap.timeline({
    scrollTrigger: {
      id: "testimonials-title",
      trigger: containerRef.current,
      start: "top 75%",
      once: true,
    },
  });

  tl.fromTo(
    titleRef.current,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      ease: "power3.out",
    }
  );

  return tl;
};

/**
 * Animates a testimonial transition
 */
export const animateTestimonialTransition = (
  quoteRef: RefType,
  authorRef: RefType
): gsap.core.Timeline | undefined => {
  if (!quoteRef.current || !authorRef.current) return;

  const elements = [quoteRef.current, authorRef.current];

  const tl = gsap.timeline();

  tl.to(elements, {
    opacity: 0,
    y: -20,
    duration: 0.3,
  })
    .set(elements, { y: 20 })
    .to(elements, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power2.out",
    });

  return tl;
};

/**
 * Animates the initial entrance of testimonial content
 */
export const animateTestimonialEntrance = (
  containerRef: RefType,
  quoteRef: RefType,
  authorRef: RefType
): gsap.core.Timeline | undefined => {
  if (!containerRef.current || !quoteRef.current || !authorRef.current) return;

  const elements = [quoteRef.current, authorRef.current];

  // Set initial state
  gsap.set(elements, { opacity: 0, y: 20 });

  const tl = gsap.timeline({
    scrollTrigger: {
      id: "testimonials-content",
      trigger: containerRef.current,
      start: "top 70%",
      once: true,
    },
  });

  tl.to(elements, {
    opacity: 1,
    y: 0,
    duration: 0.8,
    stagger: 0.2,
    ease: "power2.out",
  });

  return tl;
};

/**
 * Cleans up ScrollTrigger instances specific to the TestimonialsSlider component
 */
export const cleanupScrollTriggers = (): void => {
  ScrollTrigger.getAll()
    .filter((trigger) => {
      const id = trigger.vars.id;
      return id && id.toString().startsWith("testimonials-");
    })
    .forEach((trigger) => trigger.kill());
};
